import React from 'react';
import { Shield, Crown, Phone, User } from 'lucide-react';
import { GroupParticipant } from '../../types/groupTypes';
import { useTheme } from '../../contexts/ThemeContext';
import { formatPhone } from '../../utils/phoneUtils';

// ============================================================================
// INTERFACES
// ============================================================================

interface AdminsListProps {
  participants: GroupParticipant[];
}

// ============================================================================
// COMPONENT
// ============================================================================

export const AdminsList: React.FC<AdminsListProps> = ({ participants }) => {
  const { theme } = useTheme();

  // Filtrar apenas admins e super admins
  const admins = participants.filter(p => p.isAdmin || p.isSuperAdmin);

  // Ordenar: Super admins primeiro, depois admins
  const sortedAdmins = [...admins].sort((a, b) => {
    if (a.isSuperAdmin && !b.isSuperAdmin) return -1;
    if (!a.isSuperAdmin && b.isSuperAdmin) return 1;
    // Se ambos forem do mesmo tipo, ordenar por nome
    return (a.participantName || '').localeCompare(b.participantName || '');
  });

  if (admins.length === 0) {
    return (
      <div className="text-center py-12">
        <Shield 
          className="w-16 h-16 mx-auto mb-4 opacity-50" 
          style={{ color: 'var(--text-muted)' }} 
        />
        <p className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
          Nenhum administrador encontrado
        </p>
        <p className="text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
          Este grupo não possui administradores visíveis
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-3 border-b" 
        style={{ borderColor: 'var(--border-color)' }}>
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5" style={{ color: 'var(--text-primary)' }} />
          <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Administradores
          </h3>
          <span 
            className="px-2 py-0.5 text-xs font-normal rounded-full" 
            style={{
              backgroundColor: theme === 'dark' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.1)',
              color: theme === 'dark' ? '#86efac' : '#16a34a'
            }}
          >
            {admins.length}
          </span>
        </div>
      </div>

      {/* Lista de Admins */}
      <div className="space-y-3">
        {sortedAdmins.map((admin) => (
          <div
            key={admin.id}
            className="p-4 rounded-lg border"
            style={{
              backgroundColor: 'var(--bg-card)',
              borderColor: 'var(--border-color)'
            }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 space-y-2">
                {/* Nome */}
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-secondary)' }} />
                  <span className="text-sm font-normal" style={{ color: 'var(--text-primary)' }}>
                    {admin.participantName || 'Sem nome'}
                  </span>
                </div>

                {/* Telefone */}
                {admin.phoneNumber ? (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-secondary)' }} />
                    <span className="text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
                      {formatPhone(admin.phoneNumber)}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 flex-shrink-0 opacity-50" style={{ color: 'var(--text-muted)' }} />
                    <span className="text-sm font-normal italic" style={{ color: 'var(--text-muted)' }}>
                      Telefone privado
                    </span>
                  </div>
                )}

                {/* Badge de Hierarquia */}
                <div className="mt-2">
                  {admin.isSuperAdmin ? (
                    <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full ${
                      theme === 'dark'
                        ? 'bg-yellow-900 text-yellow-200 border border-yellow-700'
                        : 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                    }`}>
                      <Crown className="w-3 h-3" />
                      Super Admin
                    </span>
                  ) : (
                    <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-normal rounded-full ${
                      theme === 'dark'
                        ? 'bg-green-900 text-green-200'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      <Shield className="w-3 h-3" />
                      Admin
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer com contagem */}
      <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
        <p className="text-sm font-normal text-center" style={{ color: 'var(--text-muted)' }}>
          {sortedAdmins.filter(a => a.isSuperAdmin).length} Super Admin
          {sortedAdmins.filter(a => a.isSuperAdmin).length !== 1 ? 's' : ''} • {' '}
          {sortedAdmins.filter(a => a.isAdmin && !a.isSuperAdmin).length} Admin
          {sortedAdmins.filter(a => a.isAdmin && !a.isSuperAdmin).length !== 1 ? 's' : ''}
        </p>
      </div>
    </div>
  );
};

